"use client";

import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import {
  isChatDisclaimerOccluded,
  observeChatDisclaimerOcclusionLayout,
} from "./chat-disclaimer-occlusion";

/** 注意文と会話本文の DOM 位置関係を監視し、表示可否の状態を一箇所で管理します。 */
export function useChatDisclaimerOcclusion({
  rootRef,
  hasMessageContent,
  isObservationPaused,
  retainOcclusion,
}: {
  rootRef: RefObject<HTMLElement | null>;
  hasMessageContent: boolean;
  isObservationPaused: boolean;
  retainOcclusion: boolean;
}) {
  const [isOccluded, setIsOccluded] = useState(false);
  const measureOcclusion = useCallback(() => {
    const nextIsOccluded = isChatDisclaimerOccluded(rootRef.current);
    setIsOccluded((currentIsOccluded) => {
      // streaming 中は本文の伸長と smooth scroll が境界を往復するため、
      // 一度退避した注意文を応答完了まで再表示しない。
      if (retainOcclusion && currentIsOccluded) {
        return true;
      }
      return currentIsOccluded === nextIsOccluded
        ? currentIsOccluded
        : nextIsOccluded;
    });
  }, [retainOcclusion, rootRef]);

  useLayoutEffect(() => {
    if (!hasMessageContent) {
      setIsOccluded(false);
      return;
    }
    measureOcclusion();
  }, [hasMessageContent, measureOcclusion]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !hasMessageContent || isObservationPaused) return;

    return observeChatDisclaimerOcclusionLayout(root, measureOcclusion);
  }, [hasMessageContent, isObservationPaused, measureOcclusion, rootRef]);

  return { isOccluded, measureOcclusion };
}
